<script setup>
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue'
import PrimaryButton from "@/Components/PrimaryButton.vue";
import LoadingCircle from '@/Images/Animations/loading-circle.json'
import { useEnergyConfirmationStore } from "@/Stores/energyConfirmationStore";
import { useCreditStore } from "@/Stores/creditStore";
import { computed } from "vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { Vue3Lottie as Lottie } from 'vue3-lottie'

const energyConfirmationStore = useEnergyConfirmationStore();
const creditStore = useCreditStore();
const emits = defineEmits(['close']);

const props = defineProps({
    show: {
        type: Boolean,
        default: false
    },
});

function confirmationClicked() {
    energyConfirmationStore.confirmationCallback();
    energyConfirmationStore.clearEnergyData();
}

function cancelClicked() {
    if (energyConfirmationStore.confirmationCallback?.value) {
        energyConfirmationStore.confirmationCallback.value = null;
    }
    energyConfirmationStore.clearEnergyData();
    emits('close');
}

const insufficientEnergy = computed(() => {
    return energyConfirmationStore.cost > creditStore.currentBalance;
})

const isLoading = computed(() => {
    return energyConfirmationStore.cost === null;
})

const currentEnergy = computed(() => {
    if (creditStore.unlimited) {
        return "∞"
    }
    return creditStore.currentBalance
})

</script>

<template>
    <TransitionRoot as="template" :show="props.show">
        <Dialog as="div" class="relative z-10" @close="cancelClicked">
            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                <div class="fixed inset-0 bg-gray-800 bg-opacity-75 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 z-10 overflow-y-auto">
                <div class="flex min-h-full items-start justify-center p-4 text-center sm:items-center sm:p-0">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                        <DialogPanel>
                            <div class="p-6 bg-gray-700 text-left text-gray-100 w-96 overflow-y-auto" style="min-height: 10rem;">

                                <div v-if="isLoading">
                                    <Lottie v-if="isLoading" :loop="true" :animationData="LoadingCircle" style="pointer-events: none; width: 200px; margin: 0 auto;"/>
                                </div>

                                <!-- Insufficient Energy -->
                                <div v-if="insufficientEnergy && !isLoading">
                                    <h3 class="text-lg leading-6 font-medium text-gray-100 mb-6">
                                        <FontAwesomeIcon :icon="['fas', 'triangle-exclamation']" size="lg" class="mr-1"/>
                                        {{ energyConfirmationStore.title }}
                                    </h3>
                                    <h3 class="text-sm leading-6 font-medium text-gray-100 mb-6">
                                        {{ $t('forms.energyConfirmation.willConsume', { energyCost: energyConfirmationStore.cost, currentEnergy: currentEnergy }) }}
                                    </h3>
                                    <h3 class="text-sm leading-6 font-medium text-gray-100 mb-6">

                                    </h3>

                                    <div class="flex items-center justify-end mt-4">
                                        <PrimaryButton @click="cancelClicked">
                                            {{ $t('forms.energyConfirmation.close')}}
                                        </PrimaryButton>
                                    </div>
                                </div>

                                <!-- Sufficient Energy -->
                                <div v-if="!insufficientEnergy && !isLoading">
                                    <h3 class="text-lg leading-6 font-medium text-gray-100 mb-6">
                                        <FontAwesomeIcon :icon="['fas', 'badge-check']" size="lg" class="mr-1"/>
                                        {{ energyConfirmationStore.title }}
                                    </h3>
                                    <h3 class="text-sm leading-6 font-medium text-gray-100 mb-6">
                                        {{ $t('forms.energyConfirmation.willConsume', { energyCost: energyConfirmationStore.cost, currentEnergy: currentEnergy }) }}
                                    </h3>

                                    <div class="flex items-center justify-end mt-16">
                                        <PrimaryButton @click="cancelClicked" class="mr-1">
                                            {{ $t('forms.energyConfirmation.cancel')}}
                                        </PrimaryButton>
                                        <PrimaryButton @click="confirmationClicked" class="ml-1">
                                            {{ $t('forms.energyConfirmation.confirm')}}
                                        </PrimaryButton>
                                    </div>
                                </div>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

